import Vue from 'vue'
import Router from 'vue-router'
import { adminGuard, dancerGuard, dancerPrepGuard, financeAdminGuard } from './auth-guard'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  // Always land at top of page on navigation (unless browser back/forward
  // has a saved position, or the URL has a #hash target).
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return {
        selector: to.hash,
        // offset for sticky dancer app bar
        offset: { x: 0, y: 72 },
        behavior: 'smooth'
      }
    }
    // Prefer window scroll; also reset any nested scroll containers
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0)
      const main = document.querySelector('.iabf-main-content')
      if (main) {
        main.scrollTop = 0
      }
    }
    return { x: 0, y: 0 }
  },
  routes: [
    {
      // dashboard.iabf.dance root — dancer home. /iabf/dashboard kept for Stripe returns.
      path: '/',
      name: 'IABFDashboard',
      alias: '/iabf/dashboard',
      props: true,
      component: () => import('@/views/IABFDashboard/Dashboard')
    },
    {
      path: '/technique-library',
      name: 'Videos',
      component: () => import('@/views/TechniqueLibrary/Videos')
    },
    {
      path: '/video/new',
      name: 'NewVideo',
      props: true,
      component: () => import('@/views/Admin/CreateVideo')
    },
    {
      path: '/dancer/new',
      name: 'NewDancer',
      props: true,
      component: () => import('@/views/Admin/CreateDancer')
    },
    {
      path: '/video/:id',
      name: 'Video',
      props: true,
      component: () => import('@/views/TechniqueLibrary/Video')
    },
    {
      path: '/ballet-classes/new',
      name: 'NewBalletClass',
      props: true,
      component: () => import('@/views/Admin/CreateBalletClass')
    },
    {
      path: '/ballet-classes/recommendations/edit/:id',
      name: 'EditRecommendedClasses',
      props: true,
      component: () => import('@/views/Admin/EditRecommendedClasses')
    },
    {
      path: '/ballet-classes/recommendations/:id',
      name: 'RecommendedClasses',
      props: true,
      component: () => import('@/views/Admin/RecommendedClasses')
    },
    {
      path: '/ballet-classes/edit/:id',
      name: 'EditBalletClass',
      props: true,
      component: () => import('@/views/Admin/EditBalletClass')
    },
    {
      path: '/ballet-classes',
      name: 'BalletClasses',
      props: true,
      component: () => import('@/views/Admin/BalletClasses')
    },
    {
      path: '/ballet-classes/attendance/:id',
      name: 'Attendance',
      props: true,
      component: () => import('@/views/Admin/Attendance')
    },
    // IABF Admin routes — require admin auth
    {
      path: '/iabf/admin',
      name: 'IABFAdmin',
      props: true,
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/IABFDancers')
    },
    {
      path: '/iabf/admin/pieces',
      name: 'IABFPiecesAdmin',
      props: true,
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/IABFPieces')
    },
    {
      path: '/iabf/admin/pieces/new',
      name: 'NewIABFPiece',
      props: true,
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/CreateIABFPiece')
    },
    {
      path: '/iabf/showrunner',
      name: 'IABFShowRunner',
      component: () => import('@/views/Admin/ShowRunner')
    },
    {
      path: '/iabf/admin/showcase-gaps',
      name: 'IABFShowcaseGaps',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/ShowcaseGaps')
    },
    {
      path: '/iabf/admin/pieces/edit/:id',
      name: 'EditIABFPiece',
      props: true,
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/EditIABFPiece')
    },
    {
      path: '/iabf/admin/faculty',
      name: 'IABFFacultyAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/Faculty')
    },
    {
      path: '/iabf/admin/faculty/new',
      name: 'CreateIABFFaculty',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/CreateFaculty')
    },
    {
      path: '/iabf/admin/dancers/new',
      name: 'NewIABFDancer',
      props: true,
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/CreateIABFDancer')
    },
    {
      path: '/iabf/admin/dancers/edit/:id',
      name: 'EditIABFDancer',
      props: true,
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/EditIABFDancer')
    },
    {
      path: '/iabf/admin/costume-orders',
      name: 'IABFCostumeOrdersAdmin',
      props: true,
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/CostumeOrders')
    },
    {
      path: '/iabf/admin/tutu-order',
      name: 'IABFTutuOrder',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/TutuOrder')
    },
    {
      path: '/iabf/admin/borrowed-tutus',
      name: 'IABFBorrowedTutus',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/BorrowedTutus')
    },
    {
      path: '/iabf/admin/tutu-inventory-print',
      name: 'IABFTutuInventoryPrint',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/TutuInventoryPrint')
    },
    {
      path: '/iabf/admin/becoming-order',
      name: 'IABFBecomingOrder',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/BecomingOrder')
    },
    {
      path: '/iabf/admin/costume-inventory',
      name: 'IABFCostumeInventory',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/CostumeInventory')
    },
    {
      path: '/iabf/admin/inventory',
      name: 'IABFFestivalInventory',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/FestivalInventory')
    },
    {
      path: '/iabf/admin/inventory/print',
      name: 'IABFFestivalInventoryPrint',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/FestivalInventoryPrint')
    },
    {
      path: '/iabf/admin/guests',
      name: 'IABFGuestRegistrations',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/GuestRegistrations')
    },
    {
      path: '/iabf/admin/store',
      name: 'IABFStoreAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/StoreItems')
    },
    {
      path: '/iabf/admin/store/orders',
      name: 'IABFStoreOrdersAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/StoreOrders')
    },
    {
      // Open to anonymous shoppers — the store can be shared with friends
      // who don't have dancer accounts. Cart math + checkout skip
      // dancer-only benefits when no dancer is logged in.
      path: '/store',
      name: 'IABFStore',
      alias: '/iabf/store',
      component: () => import('@/views/IABFDashboard/Store')
    },
    {
      path: '/store/cart',
      name: 'IABFStoreCart',
      alias: '/iabf/store/cart',
      component: () => import('@/views/IABFDashboard/StoreCart')
    },
    {
      path: '/store/success',
      name: 'IABFStoreSuccess',
      alias: '/iabf/store/success',
      component: () => import('@/views/IABFDashboard/StoreSuccess')
    },
    {
      // Hidden for 2027 until virtual critiques reopen.
      path: '/showcase-feedback',
      name: 'IABFShowcaseFeedback',
      alias: '/iabf/showcase-feedback',
      redirect: '/'
    },
    {
      path: '/iabf/admin/showcase-feedback',
      name: 'IABFShowcaseFeedbackAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/ShowcaseFeedback')
    },
    {
      path: '/iabf/admin/addon-services',
      name: 'IABFAddonServicesAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/AddonServices')
    },
    {
      path: '/iabf/admin/photoshoots',
      name: 'IABFPhotoshootAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/PhotoshootAdmin')
    },
    {
      // Ani's judge page — secret key in the URL (?key=…) is the access
      // control. No login required.
      path: '/iabf/feedback-judge',
      name: 'IABFFeedbackJudge',
      component: () => import('@/views/IABFDashboard/FeedbackJudge')
    },
    {
      path: '/iabf/admin/competition',
      name: 'IABFCompetitionRegistrations',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/CompetitionRegistrations')
    },
    {
      path: '/iabf/admin/competition-results',
      name: 'IABFCompetitionResults',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/CompetitionResults')
    },
    {
      path: '/iabf/admin/showcase-recordings',
      name: 'IABFShowcaseRecordingsAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/ShowcaseRecordingsAdmin')
    },
    {
      path: '/iabf/admin/carpools',
      name: 'IABFCarpoolAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/IABFCarpoolAdmin')
    },
    {
      path: '/iabf/admin/carpools/print',
      name: 'IABFCarpoolPrint',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/CarpoolPrint')
    },
    {
      path: '/iabf/admin/setup-plan',
      name: 'IABFSetupPlanAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/SetupPlanAdmin')
    },
    {
      path: '/iabf/admin/welcome-bag-packing',
      name: 'IABFWelcomeBagPacking',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/WelcomeBagPacking')
    },
    {
      path: '/iabf/admin/accounting',
      name: 'IABFAccounting',
      beforeEnter: financeAdminGuard,
      component: () => import('@/views/Admin/Accounting')
    },
    {
      path: '/iabf/admin/deadlines',
      name: 'IABFDeadlinesAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/DeadlinesAdmin')
    },
    {
      path: '/iabf/admin/hold-log',
      name: 'IABFHoldLogAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/HoldLogAdmin')
    },
    {
      path: '/iabf/admin/canceled-dancers',
      name: 'IABFCanceledDancers',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/CanceledDancers')
    },
    {
      path: '/setup',
      name: 'IABFSetupPlan',
      component: () => import('@/views/IABFDashboard/SetupPlan')
    },
    {
      path: '/iabf/admin/group-pieces',
      name: 'IABFGroupPiecesAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/GroupPiecesAdmin')
    },
    {
      path: '/iabf/admin/corps-pieces',
      name: 'IABFCorpsPiecesAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/IABFCorpsPieces')
    },
    {
      path: '/iabf/admin/corps-pieces/edit/:id',
      name: 'EditIABFCorpsPiece',
      props: true,
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/EditIABFCorpsPiece')
    },
    {
      path: '/iabf/admin/choreographer-sheet',
      name: 'ChoreographerSheet',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/ChoreographerSheet')
    },
    {
      path: '/iabf/admin/rehearsal-recordings',
      name: 'IABFRehearsalRecordings',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/RehearsalRecordings')
    },
    {
      path: '/iabf/admin/woh-videos',
      name: 'WohVideosAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/WohVideosAdmin')
    },
    {
      path: '/iabf/admin/qoh-videos',
      name: 'QohVideosAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/QohVideosAdmin')
    },
    {
      path: '/iabf/admin/itinerary',
      name: 'ItineraryAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/ItineraryAdmin')
    },
    {
      path: '/iabf/admin/class-descriptions',
      name: 'ClassDescriptions',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/ClassDescriptions')
    },
    {
      path: '/itinerary',
      name: 'IABFItinerary',
      component: () => import('@/views/IABFDashboard/Itinerary')
    },
    {
      path: '/iabf/teacher-itinerary',
      name: 'TeacherItinerary',
      component: () => import('@/views/IABFDashboard/TeacherItinerary')
    },
    {
      path: '/iabf/admin/gw-videos',
      name: 'GwVideosAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/GwVideosAdmin')
    },
    {
      path: '/iabf/admin/becoming-videos',
      name: 'BecomingVideosAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/BecomingVideosAdmin')
    },
    {
      path: '/iabf/admin/jazz-videos',
      name: 'JazzVideosAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/JazzVideosAdmin')
    },
    // IABF dancer-facing routes (no /iabf prefix). Stripe return URLs stay aliased.
    {
      path: '/style-guide',
      name: 'IABFStyleGuide',
      component: () => import('@/views/IABFDashboard/StyleGuide')
    },
    {
      path: '/register',
      name: 'IABFRegister',
      alias: '/iabf/register',
      component: () => import('@/views/IABFDashboard/Register')
    },
    {
      path: '/register/success',
      name: 'IABFRegisterSuccess',
      alias: '/iabf/register/success',
      component: () => import('@/views/IABFDashboard/RegisterSuccess')
    },
    {
      path: '/guests',
      name: 'IABFGuestRegistration',
      alias: '/iabf/guests',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/GuestRegistration')
    },
    {
      path: '/profile',
      name: 'IABFProfile',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/Profile')
    },
    {
      path: '/community',
      name: 'IABFCommunity',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/Community')
    },
    {
      path: '/photoshoot',
      name: 'IABFPhotoshoot',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/Photoshoot')
    },
    {
      path: '/history',
      name: 'IABFHistory',
      beforeEnter: dancerGuard,
      component: () => import('@/views/IABFDashboard/History')
    },
    {
      path: '/closet',
      name: 'IABFCostumeCloset',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/CostumeCloset')
    },
    {
      path: '/account',
      redirect: '/profile'
    },
    {
      path: '/registration',
      name: 'IABFRegistration',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/Registration')
    },
    {
      path: '/dietary',
      name: 'IABFDietary',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/Dietary')
    },
    {
      path: '/billing',
      name: 'IABFBilling',
      alias: '/iabf/billing',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/Billing')
    },
    {
      path: '/lodging',
      name: 'IABFLodging',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/Lodging')
    },
    {
      path: '/carpools',
      name: 'IABFCarpools',
      props: true,
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/Carpools')
    },
    {
      path: '/meals',
      redirect: '/dietary'
    },
    {
      path: '/woh-videos',
      name: 'WohVideos',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/WohVideos')
    },
    {
      path: '/qoh-videos',
      name: 'QohVideos',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/QohVideos')
    },
    {
      path: '/gw-videos',
      name: 'GwVideos',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/GwVideos')
    },
    {
      path: '/becoming-videos',
      name: 'BecomingVideos',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/BecomingVideos')
    },
    {
      path: '/jazz-videos',
      name: 'JazzVideos',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/JazzVideos')
    },
    {
      path: '/group-pieces',
      name: 'IABFGroupPieces',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/GroupPieces')
    },
    {
      path: '/my-pieces',
      name: 'MyCorpsPieces',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/MyCorpsPieces')
    },
    {
      path: '/my-pieces/:pieceId',
      name: 'CorpsPieceDetail',
      props: true,
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/CorpsPieceDetail')
    },
    {
      path: '/pieces',
      name: 'IABFPieces',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/Pieces')
    },
    {
      path: '/competition',
      name: 'IABFCompetition',
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/Competition')
    },
    {
      path: '/costume-order',
      name: 'IABFCostumeOrderLookup',
      component: () => import('@/views/IABFDashboard/CostumeOrder')
    },
    {
      path: '/costume-order/:id',
      name: 'IABFCostumeOrder',
      alias: '/iabf/costume-order/:id',
      props: true,
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/CostumeOrder')
    },
    {
      path: '/costume-order/:id/success',
      name: 'IABFCostumeOrderSuccess',
      alias: '/iabf/costume-order/:id/success',
      props: true,
      beforeEnter: dancerPrepGuard,
      component: () => import('@/views/IABFDashboard/CostumeOrder')
    },
    {
      path: '/login',
      name: 'IABFLogin',
      props: true,
      component: () => import('@/views/IABFDashboard/Login')
    },
    {
      path: '/showcase',
      name: 'IABFShowcaseLineup',
      props: true,
      component: () => import('@/views/IABFDashboard/ShowcaseLineup')
    },
    {
      path: '/class-history/:email',
      name: 'ClassHistory',
      props: true,
      component: () => import('@/views/DancerDashboard/ClassHistory')
    },
    {
      path: '/bookmarks/:id',
      name: 'BookmarkedVideos',
      props: true,
      component: () => import('@/views/DancerDashboard/BookmarkedVideos')
    },
    {
      path: '/watch-history/:id',
      name: 'VideoWatchHistory',
      props: true,
      component: () => import('@/views/DancerDashboard/VideoWatchHistory')
    },
    {
      path: '/class-schedule',
      name: 'ClassSchedule',
      props: true,
      component: () => import('@/views/DancerDashboard/ClassSchedule')
    },
    {
      path: '/sign-in',
      name: 'Signin',
      component: () => import('@/views/user/Signin')
    },
    {
      path: '/sign-up',
      name: 'Signup',
      component: () => import('@/views/user/Signup')
    },
    // Pointe routes — dancer-facing (use pointe-layout via store commit in each view)
    {
      path: '/pointe',
      name: 'PointeLanding',
      component: () => import('@/views/Pointe/Landing')
    },
    {
      path: '/pointe/apply',
      name: 'PointeApply',
      component: () => import('@/views/Pointe/Apply')
    },
    {
      path: '/pointe/dashboard',
      name: 'PointeDashboard',
      beforeEnter: dancerGuard,
      component: () => import('@/views/Pointe/Dashboard')
    },
    {
      path: '/pointe/application/:id',
      name: 'PointeApplicationResult',
      props: true,
      beforeEnter: dancerGuard,
      component: () => import('@/views/Pointe/ApplicationResult')
    },
    {
      path: '/pointe/consultation/:id',
      name: 'PointeConsultationView',
      props: true,
      beforeEnter: dancerGuard,
      component: () => import('@/views/Pointe/ConsultationView')
    },
    {
      path: '/pointe/self-assessment',
      name: 'PointeSelfAssessment',
      component: () => import('@/views/Pointe/SelfAssessment')
    },
    // Pointe admin routes
    {
      path: '/pointe/admin/cohorts',
      name: 'PointeCohortsAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/PointeCohorts')
    },
    {
      path: '/pointe/admin/cohorts/new',
      name: 'PointeCreateCohort',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/CreatePointeCohort')
    },
    {
      path: '/pointe/admin/cohorts/edit/:id',
      name: 'PointeEditCohort',
      props: true,
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/CreatePointeCohort')
    },
    {
      path: '/pointe/admin/criteria',
      name: 'PointeCriteriaAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/PointeCriteria')
    },
    {
      path: '/pointe/admin/applications',
      name: 'PointeApplicationsAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/PointeApplications')
    },
    {
      path: '/pointe/admin/applications/import',
      name: 'PointeImportApplication',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/ImportPointeApplication')
    },
    {
      path: '/pointe/admin/applications/:id',
      name: 'PointeReviewApplication',
      props: true,
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/ReviewPointeApplication')
    },
    {
      path: '/pointe/admin/consultations',
      name: 'PointeConsultationsAdmin',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/PointeConsultations')
    },
    {
      path: '/pointe/admin/consultations/new',
      name: 'PointeCreateConsultation',
      beforeEnter: adminGuard,
      component: () => import('@/views/Admin/CreatePointeConsultation')
    }
  ]
})

// Bootstrap Analytics
// Set in .env
// https://github.com/MatteoGabriele/vue-analytics
if (process.env.VUE_APP_GOOGLE_ANALYTICS) {
  Vue.use(require('vue-analytics').default, {
    id: process.env.VUE_APP_GOOGLE_ANALYTICS,
    router,
    autoTracking: {
      page: process.env.NODE_ENV !== 'development'
    }
  })
}

export default router
